import {
  Button,
  DatePicker,
  Input,
  InputNumber,
  Select,
  Space,
  Table,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useMemo, useState } from 'react';
import { usePagination } from '../shared/usePagination';
import { QP, useQueryParams } from '../shared/useQueryParams';
import {
  ProductStockRow,
  useExternalOrderLocations,
  useProductStocks,
  useUpdateProductStock,
} from '../utils/api-hooks/external-order.hook';

const { Title } = Typography;
const { RangePicker } = DatePicker;

const productStocksQuerySchema = {
  productName: QP.string('productName'),
  providerName: QP.string('providerName'),
  range: QP.dayjsRange('startDate', 'endDate'),
  locationIds: QP.stringArray('locationIds'),
};

export function ProductStocksPage() {
  const pagination = usePagination({ defaultPageSize: 50, syncUrl: true });
  const { values, setValues } = useQueryParams(productStocksQuerySchema);
  const { productName, providerName, range } = values;
  const locationIds = useMemo(
    () => values.locationIds?.map(Number) ?? [],
    [values.locationIds],
  );

  const { data: locations = [] } = useExternalOrderLocations();
  const { data, isFetching } = useProductStocks({
    skip: pagination.skip,
    take: pagination.take,
    productName: productName || undefined,
    providerName: providerName || undefined,
    startDate: range.startTime,
    endDate: range.endTime,
    locationIds: locationIds.length ? locationIds : undefined,
  });
  const { mutateAsync: update, isLoading: isUpdating } = useUpdateProductStock();

  const columns: ColumnsType<ProductStockRow> = useMemo(
    () => [
      {
        title: '取餐日期',
        dataIndex: 'redeem_time',
        render: (v: string) => dayjs(v).format('YYYY-MM-DD HH:mm'),
        sorter: (a, b) =>
          dayjs(a.redeem_time).valueOf() - dayjs(b.redeem_time).valueOf(),
      },
      {
        title: '大樓',
        dataIndex: 'location_name',
        render: (v: string | null, row) => v || `#${row.location_id}`,
      },
      {
        title: '餐廳',
        dataIndex: 'provider_name',
        render: (v: string | null, row) =>
          v || (row.provider_id ? `#${row.provider_id}` : '-'),
      },
      {
        title: '商品',
        dataIndex: 'product_name',
        render: (v: string | null, row) => v || `#${row.product_id}`,
      },
      {
        title: '商品編號',
        dataIndex: 'product_code',
      },
      {
        title: '已售出',
        dataIndex: 'sold',
        align: 'right' as const,
        width: 100,
        render: (v: number | string) => Number(v ?? 0),
        sorter: (a, b) => Number(a.sold ?? 0) - Number(b.sold ?? 0),
      },
      {
        title: '庫存',
        dataIndex: 'stock',
        align: 'right' as const,
        width: 200,
        render: (_value: number, row) => (
          <StockEditor
            row={row}
            onSave={(stock) =>
              update({
                redeemTime: row.redeem_time,
                locationId: row.location_id,
                productId: row.product_id,
                stock,
              })
            }
            disabled={isUpdating}
          />
        ),
      },
    ],
    [update, isUpdating],
  );

  return (
    <Space direction="vertical" className="w-full">
      <Title level={3}>外部訂單庫存管理 (Amazon)</Title>
      <Table
        size="small"
        rowKey={(r) =>
          `${r.redeem_time}-${r.location_id}-${r.product_id}`
        }
        loading={isFetching}
        columns={columns}
        dataSource={data?.items ?? []}
        pagination={{ ...pagination, total: data?.total ?? 0 }}
        title={() => (
          <Space wrap size="middle">
            <RangePicker
              value={[range.startTime ?? null, range.endTime ?? null] as any}
              onChange={(v: any) =>
                setValues({
                  range: {
                    startTime: v?.[0] ?? undefined,
                    endTime: v?.[1] ?? undefined,
                  },
                })
              }
              showTime={{ format: 'HH:mm' }}
              format="YYYY-MM-DD HH:mm"
              placeholder={['取餐日期起', '取餐日期訖']}
              allowEmpty={[true, true]}
            />
            <Select
              mode="multiple"
              allowClear
              placeholder="大樓"
              className="min-w-[200px]"
              value={locationIds}
              onChange={(v: number[]) =>
                setValues({ locationIds: v.map(String) })
              }
              options={locations.map((l) => ({
                value: l.location_id,
                label: `${l.location_name} (#${l.location_id})`,
              }))}
            />
            <Input.Search
              key={`provider-${providerName}`}
              allowClear
              placeholder="餐廳名稱"
              defaultValue={providerName}
              onSearch={(v) => setValues({ providerName: v })}
              className="min-w-[200px]"
            />
            <Input.Search
              key={`product-${productName}`}
              allowClear
              placeholder="商品名稱 / 編號"
              defaultValue={productName}
              onSearch={(v) => setValues({ productName: v })}
              className="min-w-[240px]"
            />
          </Space>
        )}
      />
    </Space>
  );
}

function StockEditor({
  row,
  onSave,
  disabled,
}: {
  row: ProductStockRow;
  onSave: (stock: number) => Promise<unknown>;
  disabled: boolean;
}) {
  const [value, setValue] = useState<number>(row.stock);
  const dirty = value !== row.stock;

  return (
    <Space>
      <InputNumber
        min={0}
        value={value}
        onChange={(v) => setValue(typeof v === 'number' ? v : row.stock)}
        disabled={disabled}
      />
      {dirty && (
        <Button
          type="link"
          size="small"
          className="px-0"
          onClick={async () => {
            await onSave(value);
          }}
        >
          儲存
        </Button>
      )}
    </Space>
  );
}
